import type {
  ShopConfig,
  ShopProduct,
  ShopSaleRequest,
  ShopSaleResult,
  AltegioTodoItem,
  DayReport,
  PaymentDiscountInput,
  PaymentTip,
  CertificateStockRow,
  ClientCard,
  MarkAltegioRequest,
  PrintBatchView,
  GiftCertificateView,
  PastProtocolRequest,
  RecommendationRequest,
  PaymentMethod,
  ProductOffer,
  Slot,
  QuestionnaireAvailability,
  Staff,
  StaffQuestionnaireView,
  StaffVisit,
} from '@360face/domain';
import { ApiError } from '../api/client';
import { loadTabletLocale } from './messages';

export interface HotWindowsPreview {
  times: Array<{ time: string; staff: string[] }>;
  /** App users with «hot windows» on for this studio; `waiting` = not notified today yet. */
  audience: { total: number; waiting: number };
  /** Push switched on and keys set on the server. */
  enabled: boolean;
}

export interface StaffApi {
  login(locationId: string, pin: string): Promise<void>;
  logout(): void;
  readonly loggedIn: boolean;
  visits(date?: string): Promise<{ data: StaffVisit[]; date: string }>;
  /** Whole team of the location, including staff not offered at the kiosk. */
  team(): Promise<Staff[]>;
  /** Open "call staff" requests from the kiosk. */
  alerts(): Promise<StaffAlert[]>;
  resolveAlert(id: string): Promise<void>;
  visit(id: string): Promise<StaffVisit>;
  setState(id: string, to: 'checked_in' | 'in_service' | 'no_show' | 'cancelled'): Promise<StaffVisit>;
  /** "Client arrived" = the treatment starts (owner 15.10.2026): checked in, then in service. */
  arrive(id: string): Promise<StaffVisit>;
  /** Change the main treatment and/or add extras (before payment). */
  changeServices(
    id: string,
    body: { replace?: { fromServiceId: string; toServiceId: string }; addIds?: string[] },
  ): Promise<StaffVisit>;
  /** `giftHandedOver`: the free gift for the booking amount was given (required when there is one). */
  complete(
    id: string,
    performed: Array<{ serviceId: string; quantity: number; code?: string }>,
    giftHandedOver?: boolean,
  ): Promise<StaffVisit>;
  /** With `certificateCode` the certificate pays first and `method` pays the rest (if any). */
  confirmPayment(
    id: string,
    method: PaymentMethod,
    pin: string,
    nif?: string,
    certificateCode?: string,
    extras?: { discount?: PaymentDiscountInput; tip?: PaymentTip },
  ): Promise<StaffVisit>;
  /** "Sell a product" (accounting system): is it on, sellers, payment methods, discount limit. */
  shopConfig(): Promise<ShopConfig>;
  shopProducts(): Promise<ShopProduct[]>;
  shopSell(body: ShopSaleRequest): Promise<ShopSaleResult>;
  /** Products from the accounting system in the visit bill: absolute quantity (0 removes). */
  setShopProducts(id: string, items: Array<{ productId: number; qty: number }>): Promise<StaffVisit>;
  /** Book the visit's products in the accounting system again after a failure. */
  retryShopSale(id: string): Promise<StaffVisit>;
  /** Discounts (with reasons) and tips of a day. */
  dayReport(date?: string): Promise<DayReport>;
  /** Value and balance of a gift certificate of this location (404 GIFT_NOT_FOUND if unknown). */
  giftCertificate(code: string): Promise<GiftCertificateView>;
  /** Adds an older (paper) certificate to the ledger; needs the PIN again. */
  registerGiftCertificate(code: string, valueMinor: number, pin: string): Promise<GiftCertificateView>;
  /** Minutes free before the therapist's next booking (break included); null = nothing later. */
  freeMinutes(id: string): Promise<FreeMinutes>;
  /** Is there a kiosk health form for this visit or client? No health data, not logged. */
  questionnaireStatus(id: string): Promise<QuestionnaireAvailability>;
  /** Decrypted health form (null = none). Every call is recorded in the server's access log. */
  questionnaire(id: string): Promise<StaffQuestionnaireView | null>;
  /** "Proceed" / "decline" after reading a form (kept with it, without the details). */
  questionnaireDecision(id: string, decision: 'proceed' | 'decline'): Promise<StaffQuestionnaireView>;
  /** Products the therapist can add to the bill (prices from the server). */
  products(): Promise<ProductOffer[]>;
  addProducts(id: string, productIds: string[]): Promise<StaffVisit>;
  /** "Book again": dates and slots of the same therapist for the same (or given) treatments. */
  /** `staffId` (owner 17.10.2026): another therapist of the studio; default = the visit's therapist. */
  rebookDates(
    id: string,
    serviceIds?: string[],
    from?: string,
    staffId?: string,
  ): Promise<{ serviceIds: string[]; dates: string[] }>;
  rebookSlots(id: string, date: string, serviceIds?: string[], staffId?: string): Promise<Slot[]>;
  rebook(id: string, start: string, serviceIds?: string[], staffId?: string): Promise<StaffVisit>;
  /** Other bookable therapists of the studio who perform the visit's treatments (names only). */
  rebookStaff(id: string): Promise<RebookStaff[]>;
  /** Last treatments, visits count, notes, preferences (no phone or e-mail). */
  clientCard(id: string): Promise<ClientCard>;
  /** Internal note for the next therapists → end of the CRM card Note; returns the refreshed card. */
  addClientNote(id: string, text: string): Promise<ClientCard>;
  saveRecommendation(id: string, body: Partial<RecommendationRequest> & { text: string }): Promise<StaffVisit>;
  /** «Hot windows» (owner 04.11.2026): today's free times for one treatment + who would get the push. */
  hotWindows(serviceId?: string): Promise<HotWindowsPreview>;
  /** Sends the push (PIN again); returns how many phones got it. */
  sendHotWindows(times: string[], pin: string): Promise<{ sent: number }>;
  /** «Send to the app» (owner 04.11.2026): the recommendation push now, pressed by the therapist. */
  pushRecommendation(id: string): Promise<{ sent: number; devices: number; already: boolean }>;
  /** A paper protocol of an earlier visit (owner 04.11.2026) → the refreshed card. */
  addPastProtocol(id: string, body: PastProtocolRequest): Promise<ClientCard>;
  removePastProtocol(id: string, protocolId: string): Promise<ClientCard>;
  /** Printed certificates: on the shelf / sold per value, and print batches. */
  certificateStock(): Promise<CertificateStock>;
  /** New numbers to print (PIN asked again). */
  createPrintBatch(valuesMinor: number[], count: number, pin: string): Promise<PrintBatchView>;
  printBatch(batchId: string): Promise<PrintBatchView>;
  /** Sales and debits still to be repeated in Altegio by hand. */
  altegioTodo(): Promise<AltegioTodoItem[]>;
  markAltegio(code: string, what: MarkAltegioRequest): Promise<AltegioTodoItem[]>;
}

/** A therapist who could take the next visit instead (GET …/rebook/staff). */
export interface RebookStaff {
  id: string;
  displayName: string;
}

export interface CertificateStock {
  rows: CertificateStockRow[];
  batches: Array<{ batchId: string; createdAt: string; inStock: number }>;
}

export interface FreeMinutes {
  freeMinutes: number | null;
  nextStart?: string;
}

export interface StaffAlert {
  id: string;
  at: string;
  screen: string;
}

const KEY = '360face.tablet.session';

/**
 * Tablet client. The session token is kept in sessionStorage (cleared when the tab closes);
 * if storage is unavailable it stays in memory only.
 */
export function createStaffApi(baseUrl = '', fetchImpl: typeof fetch = (...a) => fetch(...a)): StaffApi {
  let token = read();

  async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(20_000),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', true);
    }
    const json = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string };
      message?: { error?: { code?: string } };
    } & T;
    if (!res.ok) {
      const code = json.error?.code ?? json.message?.error?.code ?? 'UNKNOWN';
      if (res.status === 401 && code.startsWith('SESSION')) api.logout();
      const detail = typeof json.error?.message === 'string' ? json.error.message.slice(0, 300) : undefined;
      throw new ApiError(res.status, code, res.status >= 500, detail);
    }
    return json;
  }

  const api: StaffApi = {
    get loggedIn() {
      return Boolean(token);
    },
    async login(locationId, pin) {
      const r = await call<{ data: { token: string } }>('POST', `/v1/staff/locations/${locationId}/session`, { pin });
      token = r.data.token;
      write(token);
    },
    logout() {
      token = undefined;
      write(undefined);
    },
    visits: (date) => call('GET', `/v1/staff/visits${date ? `?date=${date}` : ''}`),
    alerts: async () => (await call<{ data: StaffAlert[] }>('GET', '/v1/staff/alerts')).data,
    resolveAlert: async (id) => {
      await call('POST', `/v1/staff/alerts/${encodeURIComponent(id)}/resolve`, {});
    },
    team: async () => (await call<{ data: Staff[] }>('GET', '/v1/staff/team')).data,
    visit: async (id) => (await call<{ data: StaffVisit }>('GET', visitPath(id))).data,
    setState: async (id, to) => (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/state`, { to })).data,
    arrive: async (id) => {
      // a CRM booking gets its Hub id on the first call
      const v = (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/state`, { to: 'checked_in' })).data;
      return (await call<{ data: StaffVisit }>('POST', `${visitPath(v.id)}/state`, { to: 'in_service' })).data;
    },
    changeServices: async (id, body) =>
      (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/services`, body)).data,
    complete: async (id, performed, giftHandedOver) =>
      (
        await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/complete`, {
          performed,
          ...(giftHandedOver ? { giftHandedOver: true } : {}),
        })
      ).data,
    confirmPayment: async (id, method, pin, nif, certificateCode, extras) =>
      (
        await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/payment`, {
          method,
          pin,
          // owner 21.10.2026: the [Hub] lines in the Altegio comment in the therapist's language
          lang: loadTabletLocale(),
          ...(nif ? { nif } : {}),
          ...(certificateCode ? { certificateCode } : {}),
          ...(extras?.discount ? { discount: extras.discount } : {}),
          ...(extras?.tip ? { tip: extras.tip } : {}),
        })
      ).data,
    shopConfig: async () => (await call<{ data: ShopConfig }>('GET', '/v1/staff/shop/config')).data,
    shopProducts: async () => (await call<{ data: ShopProduct[] }>('GET', '/v1/staff/shop/products')).data,
    shopSell: async (body) => (await call<{ data: ShopSaleResult }>('POST', '/v1/staff/shop/sales', body)).data,
    setShopProducts: async (id, items) =>
      (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/shop-products`, { items })).data,
    retryShopSale: async (id) => (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/shop-sale`)).data,
    dayReport: async (date) =>
      (
        await call<{ data: DayReport }>(
          'GET',
          `/v1/staff/reports/day${date ? `?date=${encodeURIComponent(date)}` : ''}`,
        )
      ).data,
    giftCertificate: async (code) =>
      (await call<{ data: GiftCertificateView }>('GET', `/v1/staff/gift-certificates/${encodeURIComponent(code)}`))
        .data,
    certificateStock: async () =>
      (await call<{ data: CertificateStock }>('GET', '/v1/staff/gift-certificates/stock')).data,
    createPrintBatch: async (valuesMinor, count, pin) =>
      (await call<{ data: PrintBatchView }>('POST', '/v1/staff/gift-certificates/batches', { valuesMinor, count, pin }))
        .data,
    printBatch: async (batchId) =>
      (
        await call<{ data: PrintBatchView }>(
          'GET',
          `/v1/staff/gift-certificates/batches/${encodeURIComponent(batchId)}`,
        )
      ).data,
    altegioTodo: async () =>
      (await call<{ data: AltegioTodoItem[] }>('GET', '/v1/staff/gift-certificates/altegio')).data,
    markAltegio: async (code, what) =>
      (
        await call<{ data: AltegioTodoItem[] }>(
          'POST',
          `/v1/staff/gift-certificates/${encodeURIComponent(code)}/altegio`,
          what,
        )
      ).data,
    registerGiftCertificate: async (code, valueMinor, pin) =>
      (await call<{ data: GiftCertificateView }>('POST', '/v1/staff/gift-certificates', { code, valueMinor, pin }))
        .data,
    freeMinutes: async (id) => (await call<{ data: FreeMinutes }>('GET', `${visitPath(id)}/free-minutes`)).data,
    questionnaireStatus: async (id) =>
      (await call<{ data: QuestionnaireAvailability }>('GET', `${visitPath(id)}/questionnaire/status`)).data,
    questionnaire: async (id) =>
      (await call<{ data: StaffQuestionnaireView | null }>('GET', `${visitPath(id)}/questionnaire`)).data,
    questionnaireDecision: async (id, decision) =>
      (
        await call<{ data: StaffQuestionnaireView }>('POST', `${visitPath(id)}/questionnaire/decision`, {
          decision,
          lang: loadTabletLocale(),
        })
      ).data,
    products: async () => (await call<{ data: ProductOffer[] }>('GET', '/v1/staff/products')).data,
    addProducts: async (id, productIds) =>
      (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/products`, { productIds })).data,
    rebookDates: async (id, serviceIds, from, staffId) => {
      const q = [
        serviceIds && serviceIds.length > 0 ? `serviceIds=${encodeURIComponent(serviceIds.join(','))}` : '',
        from ? `from=${encodeURIComponent(from)}` : '',
        staffId ? `staffId=${encodeURIComponent(staffId)}` : '',
      ].filter(Boolean);
      return (
        await call<{ data: { serviceIds: string[]; dates: string[] } }>(
          'GET',
          `${visitPath(id)}/rebook/dates${q.length ? `?${q.join('&')}` : ''}`,
        )
      ).data;
    },
    rebookSlots: async (id, date, serviceIds, staffId) =>
      (
        await call<{ data: Slot[] }>(
          'GET',
          `${visitPath(id)}/rebook/slots?date=${date}${idsQuery(serviceIds, '&')}${
            staffId ? `&staffId=${encodeURIComponent(staffId)}` : ''
          }`,
        )
      ).data,
    rebookStaff: async (id) => (await call<{ data: RebookStaff[] }>('GET', `${visitPath(id)}/rebook/staff`)).data,
    clientCard: async (id) => (await call<{ data: ClientCard }>('GET', `${visitPath(id)}/client-card`)).data,
    addClientNote: async (id, text) =>
      (await call<{ data: ClientCard }>('POST', `${visitPath(id)}/client-note`, { text })).data,
    saveRecommendation: async (id, body) =>
      (await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/recommendation`, body)).data,
    hotWindows: async (serviceId) =>
      (
        await call<{ data: HotWindowsPreview }>(
          'GET',
          `/v1/staff/hot-windows${serviceId ? `?serviceId=${encodeURIComponent(serviceId)}` : ''}`,
        )
      ).data,
    sendHotWindows: async (times, pin) =>
      (await call<{ data: { sent: number } }>('POST', '/v1/staff/hot-windows', { times, pin })).data,
    pushRecommendation: async (id) =>
      (
        await call<{ data: { sent: number; devices: number; already: boolean } }>(
          'POST',
          `${visitPath(id)}/recommendation/push`,
        )
      ).data,
    addPastProtocol: async (id, body) =>
      (await call<{ data: ClientCard }>('POST', `${visitPath(id)}/past-protocol`, body)).data,
    removePastProtocol: async (id, protocolId) =>
      (
        await call<{ data: ClientCard }>(
          'POST',
          `${visitPath(id)}/past-protocol/${encodeURIComponent(protocolId)}/remove`,
        )
      ).data,
    rebook: async (id, start, serviceIds, staffId) =>
      (
        await call<{ data: StaffVisit }>('POST', `${visitPath(id)}/rebook`, {
          start,
          ...(serviceIds ? { serviceIds } : {}),
          ...(staffId ? { staffId } : {}),
        })
      ).data,
  };
  return api;
}

function idsQuery(ids: string[] | undefined, sep = '?'): string {
  return ids && ids.length > 0 ? `${sep}serviceIds=${encodeURIComponent(ids.join(','))}` : '';
}

/** StaffVisit ids from the CRM look like `crm:2026-10-15:677254326`, so they are always encoded. */
function visitPath(id: string): string {
  return `/v1/staff/visits/${encodeURIComponent(id)}`;
}

function read(): string | undefined {
  try {
    return sessionStorage.getItem(KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function write(v: string | undefined): void {
  try {
    if (v) sessionStorage.setItem(KEY, v);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* memory only */
  }
}
